// D45 Item 3b / D49 — GOAL precision-aim mode, now an EXPLICIT hold: RMB while carrying (anywhere on
// the pitch) remaps the mouse to place a point on the goal's entry plane (goalAim = {y, z}) at a damped
// ratio, while the camera eases to keep facing the goal. Outputs full 2D shot placement — aimYaw
// (horizontal) + aimElev01 (solved so the shot arrives at goalAim.z, via aimElevFor). Releasing RMB (or
// losing the ball) exits — no box gating, no flick-breaks, no cooldowns (all D47 auto-lock machinery
// retired by the D49 designer order). Pure/deterministic (no DOM); the sim stays untouched (§3 —
// aimYaw/aimElev remain client-authored inputs).

export interface GoalDescriptor {
  lineX: number; // x of the opponent goal line (attackDir · L/2)
  attackSign: number; // +1 if the goal is at +x, −1 at −x
  halfWidth: number; // goalWidth / 2
  barH: number; // crossbar height
}

export interface GoalLockConfig {
  AIM_SENS: number; // px → metres on the plane (damped precision ratio)
  MARGIN_W: number; // usable width fraction inside the posts (0..1)
  MARGIN_H: number; // usable height fraction below the bar (0..1)
}

export const DEFAULT_GOAL_LOCK: GoalLockConfig = {
  AIM_SENS: 0.028, // D47: ~170 px post-to-post
  MARGIN_W: 0.95,
  MARGIN_H: 0.95,
};

/** FAFI Touch (plan §2.3): thumb-drag sensitivity — a thumb has roughly a third of a mouse's usable
 *  travel, so the ratio coarsens (~120 px spans the mouth); the margins are unchanged. */
export const TOUCH_GOAL_LOCK: GoalLockConfig = {
  AIM_SENS: 0.045,
  MARGIN_W: 0.95,
  MARGIN_H: 0.95,
};

export interface GoalLockOutput {
  locked: boolean;
  aimYaw: number; // horizontal placement (toward goalAim.y on the goal line)
  aimElev01: number; // solved loft so the shot arrives at goalAim.z
  reticle: { x: number; y: number; z: number } | null; // world-space on-plane marker (null when not locked)
}

/** D45 Item 3b: which goal does the controlled team attack? The client has no attackDir on the wire, so
 *  infer it from the keepers — the OPPONENT keeper stands at the opponent goal; fall back to inverting our
 *  own keeper's side. Returns null when neither keeper x is known (mid-restart) ⇒ the lock won't engage. */
export function goalFromKeepers(
  ownGkX: number | null,
  oppGkX: number | null,
  pitch: { length: number; goalWidth: number; barH: number },
): GoalDescriptor | null {
  let sign: number;
  if (oppGkX !== null) sign = Math.sign(oppGkX) || 1;
  else if (ownGkX !== null) sign = -(Math.sign(ownGkX) || 1);
  else return null;
  return {
    lineX: (sign * pitch.length) / 2,
    attackSign: sign,
    halfWidth: pitch.goalWidth / 2,
    barH: pitch.barH,
  };
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export class GoalAimLock {
  private locked = false;
  private goalAim = { y: 0, z: 0 };

  constructor(private readonly cfg: GoalLockConfig = DEFAULT_GOAL_LOCK) {}

  get isLocked(): boolean {
    return this.locked;
  }

  /**
   * Advance one frame. `engageHeld` = the goal-aim hold (the LMB/shoot hold since D137 §7.3);
   * `solveElev(distM, targetZ)` = aimElevFor bound to SHOOT at full power. Locked while held + a goal is
   * known + `canAim`; when not locked, aimYaw = camYaw and aimElev01 = 0 (free look, caller's own aim).
   *
   * D130: `canAim` used to be `hasBall`, which is false at EVERY placed restart — a dead ball has
   * `possessor === null` — so no aiming aid existed at the one moment aim matters most. The caller now
   * passes `hasBall || isDesignatedTaker`. The gate stays a plain boolean here on purpose: this module
   * knows about goals and mice, not about restarts.
   */
  update(
    carrier: { x: number; y: number },
    camYaw: number,
    goal: GoalDescriptor | null,
    mouseDx: number,
    mouseDy: number,
    canAim: boolean,
    engageHeld: boolean,
    solveElev: (distM: number, targetZ: number) => number,
  ): GoalLockOutput {
    const free: GoalLockOutput = { locked: false, aimYaw: camYaw, aimElev01: 0, reticle: null };
    if (!goal || !canAim || !engageHeld) {
      this.locked = false;
      return free;
    }
    const halfW = goal.halfWidth * this.cfg.MARGIN_W;
    const topZ = goal.barH * this.cfg.MARGIN_H;

    if (!this.locked) {
      // engage: seed goalAim from the camera-ray ∩ goal plane, clamped INSIDE the frame (80 % of the
      // mouth — never on a post, so the first mouse move reads; D47)
      const dx = (goal.lineX - carrier.x) * goal.attackSign;
      const t = dx > 0.5 ? (goal.lineX - carrier.x) / Math.cos(camYaw) : 0; // behind the line ⇒ centre seed
      const y0 = Number.isFinite(t) && t > 0 ? carrier.y + Math.sin(camYaw) * t : 0;
      this.locked = true;
      this.goalAim = { y: clamp(y0, -halfW * 0.8, halfW * 0.8), z: clamp(goal.barH * 0.4, 0.1, topZ) };
    } else {
      // held: mouse moves the POINT on the plane (not the camera yaw), damped; up = higher.
      // D51: "mouse right" = SCREEN right. The scene maps sim (x,y)→three (x,z) with a y-up camera, so
      // screen-right is yaw+90° (= +y when attacking +x) — the OPPOSITE of the math convention D47
      // assumed; the sign is +attackSign, verified against the camera basis in scene.ts.
      const rightSign = goal.attackSign;
      this.goalAim.y = clamp(this.goalAim.y + rightSign * mouseDx * this.cfg.AIM_SENS, -halfW, halfW);
      this.goalAim.z = clamp(this.goalAim.z - mouseDy * this.cfg.AIM_SENS, 0.05, topZ);
    }

    const outY = this.goalAim.y;
    const outZ = this.goalAim.z;
    const dist = Math.hypot(goal.lineX - carrier.x, outY - carrier.y);
    return {
      locked: true,
      aimYaw: Math.atan2(outY - carrier.y, goal.lineX - carrier.x),
      aimElev01: solveElev(dist, outZ),
      reticle: { x: goal.lineX - goal.attackSign * 0.05, y: outY, z: outZ },
    };
  }
}
